import { Component, Inject, OnDestroy, OnInit, AfterViewInit, DOCUMENT } from '@angular/core';

import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { navigation } from 'app/navigation/navigation';
import { locale as navigationEnglish } from 'app/navigation/i18n/en';
import { locale as navigationTurkish } from 'app/navigation/i18n/tr';
import { AuthenticationService, UserService, StorageService, AlertService, SocketService } from './main/apps/_services';
import { User } from './main/apps/_models';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    currentUser: User;
    LoginUserDetails: User;
    fuseConfig: any;
    navigation: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private authenticationService: AuthenticationService,
        private userService: UserService,
        private storageService: StorageService,
        private alertService: AlertService,
        private socketService: SocketService
    ) {

        this.authenticationService.currentUser.subscribe(x => this.currentUser = x);
        // Get default navigation
        this.navigation = navigation;
        // Register the navigation to the service
        this._fuseNavigationService.register('main', this.navigation);
        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');
        // Add languages
        this._translateService.addLangs(['en', 'tr']);
        // Set the default language
        this._translateService.setDefaultLang('en');
        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish, navigationTurkish);
        // Use a language
        this._translateService.use('en');
        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }
        this.authenticationService.currentUserObservable().subscribe((res: any) => {
            this.currentUser = res
            if (this.currentUser && this.currentUser.UserDetails !== null) {
                setTimeout(() => {
                    if (!this.currentUser.UserDetails.ProfilePicture) {
                        // console.log("profile", this.currentUser.UserDetails.ProfilePicture);
                        this.alertService.success('Please add your picture to complete profile', false, 5000);
                    }
                }, 1000);
                if (!this.currentUser.UserDetails.ProfilePicture) {
                    //console.log("profile", this.currentUser.UserDetails.ProfilePicture);
                    this.alertService.success('Please add your picture to complete profile');
                }
                this.initSocket();
            }
        });
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                }
                else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });
        const uaInfo = this.storageService.getItem('uaInfo');
        if (!uaInfo) {
            this.getUserAgent();
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }
    ngAfterViewInit() {
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }
    getUserAgent() {
        const reqData = { 'GetUserAgentID': { 'Resolution': screen.width + 'x' + screen.height } };
        this.userService.getUserAgentID(reqData).subscribe((res: any) => {
            if (res.ResponseCode == 200) {
                res.Data.ClientTZDIFF = this.getClientTimezoneName();
                this.storageService.setItem('uaInfo', res.Data);
            } else {
                this.alertService.error(res.Message);
            }
        });
    }
    getClientTimezoneName() {
        var d = new Date();
        var tz = d.getTimezoneOffset() * 60;
        if (tz < 0)
            return Math.abs(tz);
        else
            return Math.abs(tz) * -1;
    }
    initSocket() {
        // console.log('[SOC] App initSocket');
        this.socketService.getSocketData();
        this.getNotifications();
    }
    getNotifications() {
        const uaInfo = this.storageService.getItem('uaInfo');
        const reqData = {
            'getNotifications': {
                "ClientTZDIFF": uaInfo.ClientTZDIFF
            }
        }
        this.socketService.getSocketData(reqData, () => {
            console.log('Notification called')
        });

    }
    /* logout() {
         this.authenticationService.logout();
         this.router.navigate(['/apps/login']);
     }*/
}
